import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Store, Plus, Trash2, Save, Loader2, Edit, Package, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_TYPES = ['datasheet', 'manual', 'video', 'guide', 'download', 'tool', 'link'];
const emptyResource = { title: '', description: '', url: '', type: 'datasheet' };
const emptyFeature = { title: '', description: '' };

export default function AdminVendors() {
  const { token } = useContext(AuthContext);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [expandedSection, setExpandedSection] = useState('resources');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', about_text: '', logo_url: '', website: '', enabled: true, sort_order: 0,
    highlights: [], features: [], resources: []
  });
  const [resourceForm, setResourceForm] = useState({ ...emptyResource });
  const [editingResource, setEditingResource] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [featureForm, setFeatureForm] = useState({ ...emptyFeature });
  const [editingFeature, setEditingFeature] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchVendors(); }, []);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/vendors`, authHeaders);
      setVendors(res.data.vendors || []);
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Vendor name required'); return; }
    setSaving(true);
    try {
      if (editingVendor) {
        await axios.put(`${API}/api/admin/vendors/${editingVendor.id}`, form, authHeaders);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/api/admin/vendors`, form, authHeaders);
        toast.success('Vendor created');
      }
      setShowModal(false);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete "${vendor.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/vendors/${vendor.id}`, authHeaders);
      toast.success('Deleted');
      fetchVendors();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setForm({ ...vendor, features: vendor.features || [], resources: vendor.resources || [], highlights: vendor.highlights || [] });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingVendor(null);
    setForm({ name: '', description: '', about_text: '', logo_url: '', website: '', enabled: true, sort_order: 0, highlights: [], features: [], resources: [] });
    setShowModal(true);
  };

  const seedVendors = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/vendors/seed`, {}, authHeaders);
      res.data.success ? toast.success('Vendors seeded') : toast.info(res.data.message);
      fetchVendors();
    } catch { toast.error('Failed to seed'); }
  };

  // Resource management
  const openResourceModal = (vendor, resource = null, idx = null) => {
    setEditingVendor(vendor);
    setEditingResource(idx);
    setResourceForm(resource ? { ...resource } : { ...emptyResource });
    setShowResourceModal(true);
  };

  const saveResource = async () => {
    if (!resourceForm.title.trim() || !resourceForm.url.trim()) { toast.error('Title and URL required'); return; }
    const vendor = editingVendor;
    let resources = [...(vendor.resources || [])];
    if (editingResource !== null) {
      resources[editingResource] = resourceForm;
    } else {
      resources.push(resourceForm);
    }
    try {
      await axios.put(`${API}/api/admin/vendors/${vendor.id}`, { resources }, authHeaders);
      toast.success(editingResource !== null ? 'Resource updated' : 'Resource added');
      setShowResourceModal(false);
      fetchVendors();
    } catch { toast.error('Failed to save'); }
  };

  const deleteResource = async (vendor, idx) => {
    if (!window.confirm('Delete this resource?')) return;
    const resources = (vendor.resources || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/api/admin/vendors/${vendor.id}`, { resources }, authHeaders);
      toast.success('Deleted');
      fetchVendors();
    } catch { toast.error('Failed'); }
  };

  // Feature management
  const openFeatureModal = (vendor, feature = null, idx = null) => {
    setEditingVendor(vendor);
    setEditingFeature(idx);
    setFeatureForm(feature ? { ...feature } : { ...emptyFeature });
    setShowFeatureModal(true);
  };

  const saveFeature = async () => {
    if (!featureForm.title.trim()) { toast.error('Title required'); return; }
    const vendor = editingVendor;
    let features = [...(vendor.features || [])];
    if (editingFeature !== null) {
      features[editingFeature] = featureForm;
    } else {
      features.push(featureForm);
    }
    try {
      await axios.put(`${API}/api/admin/vendors/${vendor.id}`, { features }, authHeaders);
      toast.success(editingFeature !== null ? 'Feature updated' : 'Feature added');
      setShowFeatureModal(false);
      fetchVendors();
    } catch { toast.error('Failed'); }
  };

  const deleteFeature = async (vendor, idx) => {
    if (!window.confirm('Delete this feature?')) return;
    const features = (vendor.features || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/api/admin/vendors/${vendor.id}`, { features }, authHeaders);
      toast.success('Deleted');
      fetchVendors();
    } catch { toast.error('Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-vendors-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight flex items-center gap-3">
              <Store className="h-8 w-8 text-[#78BE20]" /> Vendors
            </h1>
            <p className="text-zinc-400 mt-1">Manage vendor profiles, resources, videos, and documentation</p>
          </div>
          <div className="flex gap-2">
            {vendors.length === 0 && (
              <Button onClick={seedVendors} variant="outline" className="border-zinc-700" data-testid="seed-btn">Seed Defaults</Button>
            )}
            <Button onClick={handleNew} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="add-vendor-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Vendor
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2].map(i => <div key={i} className="h-24 bg-zinc-800 rounded-xl animate-pulse" />)}</div>
        ) : vendors.length === 0 ? (
          <Card className="bg-[#18181b] border-zinc-800"><CardContent className="py-12 text-center">
            <Store className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No vendors yet.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {vendors.map(vendor => (
              <Card key={vendor.id} className="bg-[#18181b] border-zinc-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        {vendor.name}
                        {!vendor.enabled && <Badge variant="outline" className="border-red-700 text-red-400 text-xs">Disabled</Badge>}
                      </CardTitle>
                      <p className="text-sm text-zinc-500 mt-0.5">
                        {(vendor.resources || []).length} resources | {(vendor.features || []).length} features
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id)}>
                        <Package className="h-4 w-4 mr-1" /> Manage
                        {expandedVendor === vendor.id ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => handleEdit(vendor)} data-testid={`edit-vendor-${vendor.slug}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-red-400" onClick={() => handleDelete(vendor)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {expandedVendor === vendor.id && (
                  <CardContent className="pt-0 space-y-4">
                    {/* Section tabs */}
                    <div className="flex gap-2 border-t border-zinc-800 pt-3">
                      {['resources', 'features'].map(s => (
                        <button key={s} onClick={() => setExpandedSection(s)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${expandedSection === s ? 'bg-[#78BE20] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
                          {s === 'resources' ? `Resources (${(vendor.resources || []).length})` : `Features (${(vendor.features || []).length})`}
                        </button>
                      ))}
                    </div>

                    {/* Resources Section */}
                    {expandedSection === 'resources' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-zinc-300">Videos, Datasheets & Documents</span>
                          <Button size="sm" onClick={() => openResourceModal(vendor)} className="bg-zinc-800 hover:bg-zinc-700 text-xs">
                            <Plus className="h-3 w-3 mr-1" /> Add Resource
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {(vendor.resources || []).map((res, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 border border-zinc-800">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]">{res.type}</Badge>
                                  <span className="text-sm font-medium text-white truncate">{res.title}</span>
                                </div>
                                <p className="text-xs text-zinc-600 truncate mt-0.5">{res.url}</p>
                              </div>
                              <div className="flex gap-1 ml-2">
                                <a href={res.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-zinc-500 hover:text-[#78BE20]"><ExternalLink className="h-3.5 w-3.5" /></a>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-500 hover:text-white" onClick={() => openResourceModal(vendor, res, idx)}><Edit className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" onClick={() => deleteResource(vendor, idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </div>
                          ))}
                          {(!vendor.resources || vendor.resources.length === 0) && <p className="text-xs text-zinc-600 text-center py-4">No resources. Add videos, datasheets, and guides.</p>}
                        </div>
                      </div>
                    )}

                    {/* Features Section */}
                    {expandedSection === 'features' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-zinc-300">Key Features / Selling Points</span>
                          <Button size="sm" onClick={() => openFeatureModal(vendor)} className="bg-zinc-800 hover:bg-zinc-700 text-xs">
                            <Plus className="h-3 w-3 mr-1" /> Add Feature
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {(vendor.features || []).map((f, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 border border-zinc-800">
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-white">{f.title}</span>
                                <p className="text-xs text-zinc-500 truncate">{f.description}</p>
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-500 hover:text-white" onClick={() => openFeatureModal(vendor, f, idx)}><Edit className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" onClick={() => deleteFeature(vendor, idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </div>
                          ))}
                          {(!vendor.features || vendor.features.length === 0) && <p className="text-xs text-zinc-600 text-center py-4">No features yet.</p>}
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Vendor Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
            <div><Label>Short Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} /></div>
            <div><Label>About (detailed)</Label><Textarea value={form.about_text || ''} onChange={(e) => setForm({ ...form, about_text: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={4} placeholder="Detailed information shown on the vendor page. Use double newlines for paragraphs." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="https://" /></div>
              <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
              <div className="flex items-center gap-3 pt-6"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /><Label>Enabled</Label></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resource Modal */}
      <Dialog open={showResourceModal} onOpenChange={setShowResourceModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editingResource !== null ? 'Edit Resource' : 'Add Resource'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Title *</Label><Input value={resourceForm.title} onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="resource-title" /></div>
            <div><Label>URL *</Label><Input value={resourceForm.url} onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="https://" /></div>
            <div><Label>Type</Label>
              <Select value={resourceForm.type} onValueChange={(v) => setResourceForm({ ...resourceForm, type: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {RESOURCE_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Input value={resourceForm.description} onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowResourceModal(false)} className="border-zinc-700">Cancel</Button>
              <Button onClick={saveResource} className="bg-[#78BE20] text-black hover:bg-[#65a31b]"><Save className="h-4 w-4 mr-2" /> Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feature Modal */}
      <Dialog open={showFeatureModal} onOpenChange={setShowFeatureModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editingFeature !== null ? 'Edit Feature' : 'Add Feature'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Title *</Label><Input value={featureForm.title} onChange={(e) => setFeatureForm({ ...featureForm, title: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
            <div><Label>Description</Label><Textarea value={featureForm.description} onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={3} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowFeatureModal(false)} className="border-zinc-700">Cancel</Button>
              <Button onClick={saveFeature} className="bg-[#78BE20] text-black hover:bg-[#65a31b]"><Save className="h-4 w-4 mr-2" /> Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
